import { Form } from 'antd';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyTestCaptchaQuery } from '@app/api/Captcha';
import type { PhoneValidation } from '@app/components/Phone/types';
import { FRONT_WARNING, SUCCESS } from '@app/constants';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useRegisterNewMutation } from '@app/services/rtkQuery/organization';
import type { NOrganization } from '@app/types/Organization';
import { clog } from '@app/utils/clog';

import type { RegistrationFormValues } from '@pages/Registration/Registration.types';

const { useForm } = Form;
export const useRegistration = () => {
  const navigate = useNavigate();
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });

  const { showNotification } = useContext(NotificationContext);
  const [form] = useForm<RegistrationFormValues>();
  const { setFields } = form;
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaId, setCaptchaId] = useState<string>('');
  const [isCorrectCaptchaAnswer, setIsCorrectCaptchaAnswer] = useState(true);
  const [userCaptchaValue, setUserCaptchaValue] = useState(0);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);

  const [registerNew, { isLoading: isRegisterNewOrganizationLoading }] =
    useRegisterNewMutation();

  const [triggerTestCaptchaQuery, { isLoading: isTestCaptchaLoading }] =
    useLazyTestCaptchaQuery();

  const makeRegisterNewOrganizationPayload: (
    values: RegistrationFormValues
  ) => NOrganization.RegisterNewOrganizationPayload | undefined = useCallback(
    (values: RegistrationFormValues) => {
      for (const key in values) {
        if (values.hasOwnProperty(key)) {
          const value = values[key as keyof RegistrationFormValues];

          if (!value) {
            clog('error', 'Отсутствует обязательное поле!');
            return;
          }

          if (!selectedCityId) {
            clog('error', 'Отсутствует id города!');
            return;
          }

          return {
            OwnerName: values.firstName,
            OwnerSurename: values.lastName,
            OwnerPhone: values.Phone.Phone,
            OwnerEmail: values.email,
            CompanyName: values.organization,
            CityFiasId: selectedCityId,
            TimezoneId: values.timezone,
          };
        }
      }
    },
    [selectedCityId]
  );

  const onFormFinish = useCallback(
    async (values: RegistrationFormValues) => {
      const { errorMessage, isValid } = phoneValidation;

      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        setFields([
          {
            name: 'Phone',
            errors: [phoneErrorMessage],
          },
        ]);
        return;
      }

      const { data, isError: isTestCaptchaError } =
        await triggerTestCaptchaQuery({
          captchaId: captchaId,
          formulaResult: userCaptchaValue,
          minLifetime: 2,
        });

      if (isTestCaptchaError) {
        showNotification('Ошибка в методе проверки капчи!', FRONT_WARNING);
        return;
      }

      const isCorrectCaptchaAnswer = !!data?.TestResult;

      setIsCorrectCaptchaAnswer(isCorrectCaptchaAnswer);

      if (!isCorrectCaptchaAnswer) return;

      const payload = makeRegisterNewOrganizationPayload(values);

      try {
        if (!payload) return;

        const isErrorBoxRendered = document.querySelector(
          '.ui-text-container.ui-input-description'
        );

        if (isErrorBoxRendered) {
          return;
        }

        await registerNew(payload).unwrap();

        showNotification('Регистрация организации прошла успешно!', SUCCESS);

        navigate(`/authorization?email=${encodeURIComponent(values.email)}`);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      triggerTestCaptchaQuery,
      captchaId,
      userCaptchaValue,
      makeRegisterNewOrganizationPayload,
      registerNew,
      showNotification,
      navigate,
      setFields,
      phoneValidation,
    ]
  );

  return {
    isButtonLoading: isRegisterNewOrganizationLoading || isTestCaptchaLoading,
    isCorrectCaptchaAnswer,
    captchaImage,
    setCaptchaImage,
    setUserCaptchaValue,
    selectedCityId,
    setSelectedCityId,
    setCaptchaId,
    onFormFinish,
    form,
    setPhoneValidation,
    phoneValidation,
  };
};
